import { z } from "zod";
import type { InvitationBulkInput, InvitationInput, InvitationTermsInput } from "@/lib/queries";
import { userSkillsPayload } from "@/lib/skills";
import {
  DISPLAY_NAME_MAX, ROLE_LABELS, type Invitation, type InvitationBulkResult, type Role, type RoutingSkill,
} from "@/lib/types";

/**
 * Invitations as the console shows and sends them (ADR-028 in `platform`). The server decides an
 * invitation's status and whether its email went; these helpers say it, and build the body
 * `POST /invitations` takes. Pure, for `test/invitations.test.ts`.
 */

/** `MIN_PASSWORD_LENGTH` in core-api's `schemas.py`, which refuses anything shorter. */
export const MIN_PASSWORD = 12;

/** The token in a link's fragment, `/invite#<token>`. Empty when there is none to read. */
export function tokenFromHash(hash: string): string {
  try {
    return decodeURIComponent(hash.replace(/^#/, "")).trim();
  } catch {
    return "";
  }
}

/** "a rep", "an admin": a role as a sentence names it. */
export function withArticle(role: Role): string {
  return role === "admin" ? "an admin" : `a ${role}`;
}

export interface InviteValues {
  email: string;
  /** Their real name (ADR-172 in `platform`). Only the form for one person asks for it apart from
   *  the name they go by; a list's one name is both. */
  name?: string;
  display_name: string;
  role: Role;
  domain_id: number | null;
  endpoint_domain_id: number | null;
  campaign_ids: readonly string[];
  /** slug to level, as the skill checklist holds them. */
  skills: Readonly<Record<string, number>>;
}

/** Where the account's phone will be made: its own domain for a rep or a supervisor, the chosen
 *  one for an admin or a superadmin, who span domains. Null means no phone. */
export function phoneDomainOf(values: Pick<InviteValues, "role" | "domain_id" | "endpoint_domain_id">): number | null {
  return values.role === "supervisor" || values.role === "rep" ? values.domain_id : values.endpoint_domain_id;
}

/**
 * The scope a role needs where the console has one configured domain: a supervisor or a rep is
 * locked to it, an admin spans the tenant and is told to put a phone in it, and a superadmin
 * belongs to no domain and is given no phone here. Mirrors `schemas.role_scope` in `platform`,
 * which works the same thing out for a batch row that names a role and no scope.
 */
export function scopeForRole(
  role: Role,
  domainId: number | null,
): Pick<InviteValues, "domain_id" | "endpoint_domain_id"> {
  const locked = role === "supervisor" || role === "rep";
  return {
    domain_id: locked ? domainId : null,
    endpoint_domain_id: role === "admin" ? domainId : null,
  };
}

/** Whether the account can staff campaigns and hold skills. Both belong to the phone the ACD rings,
 *  and a superadmin belongs to no tenant, so has no catalog to take them from. */
export function staffable(role: Role, phoneDomainId: number | null): boolean {
  return role !== "superadmin" && phoneDomainId !== null;
}

/** The tenant every row belongs to, or null when there is none or several. A superadmin reads every
 *  tenant's campaigns and skills, and invites into the sole tenant. */
export function soleTenantId(rows: readonly { tenant_id: number }[]): number | null {
  const ids = [...new Set(rows.map((row) => row.tenant_id))];
  return ids.length === 1 ? ids[0]! : null;
}

/**
 * What an invitation makes of the person: a domain only for the roles locked to one, a phone domain
 * only for the two that are not, and campaigns and skills only where the account will have a phone
 * to put them on, since the server refuses them otherwise. The tenant is the server's to fill in.
 */
export function invitationTerms(
  values: Omit<InviteValues, "email" | "name" | "display_name">,
  assignable: readonly Pick<RoutingSkill, "slug">[],
): InvitationTermsInput {
  const locked = values.role === "supervisor" || values.role === "rep";
  const staffs = staffable(values.role, phoneDomainOf(values));
  return {
    role: values.role,
    domain_id: locked ? values.domain_id : null,
    ...(!locked && values.endpoint_domain_id !== null
      ? { endpoint_domain_id: values.endpoint_domain_id }
      : {}),
    campaign_ids: staffs ? [...new Set(values.campaign_ids)] : [],
    skills: staffs ? userSkillsPayload(values.skills, assignable).skills : [],
  };
}

/** The body of `POST /invitations`. */
export function invitationPayload(
  values: InviteValues,
  assignable: readonly Pick<RoutingSkill, "slug">[],
): InvitationInput {
  return {
    email: values.email.trim(),
    ...(values.name?.trim() ? { name: values.name.trim() } : {}),
    display_name: values.display_name.trim() || null,
    ...invitationTerms(values, assignable),
  };
}

// --- several at once ------------------------------------------------------------------------------

/** `MAX_BULK_INVITATIONS` in core-api's `schemas.py`, which refuses a longer list. */
export const MAX_BULK_INVITATIONS = 20;

/** The role, campaigns and skills one person joins on. The list's own terms, when a row has been
 *  given none, are the ones below the list. */
export type PersonTerms = Omit<InviteValues, "email" | "name" | "display_name">;

/** Somebody on the list of people to invite: the name the admin will send, and their own terms
 *  once the admin has opened the row and set any. Null means "whatever the list says". */
export interface BulkPerson {
  email: string;
  display_name: string;
  terms?: PersonTerms | null;
}

/** What this person actually joins on: their own terms, or the list's. */
export function personTerms(person: BulkPerson, shared: PersonTerms): PersonTerms {
  return person.terms ?? shared;
}

/** "Rep, 2 campaigns, 1 skill": what a row will send, for the row to show without being opened. */
export function personSummary(
  terms: PersonTerms,
  campaigns: readonly { id: string; name: string }[],
  assignable: readonly Pick<RoutingSkill, "slug">[],
): string {
  const staffs = staffable(terms.role, phoneDomainOf(terms));
  const picked = staffs ? campaigns.filter((c) => terms.campaign_ids.includes(c.id)) : [];
  const skills = staffs ? userSkillsPayload(terms.skills, assignable).skills : [];
  const count = (n: number, one: string) => (n === 1 ? `1 ${one}` : `${n} ${one}s`);
  return [
    ROLE_LABELS[terms.role],
    picked.length === 1 ? picked[0]!.name : staffs ? count(picked.length, "campaign") : null,
    staffs ? count(skills.length, "skill") : null,
  ]
    .filter(Boolean)
    .join(", ");
}

/**
 * A name read from an address, for the admin to keep or change: the part before the @, where a dot
 * or an underscore parts the words, so "john.smith" is "John Smith". A "+tag" is dropped, and so is
 * a part that is only digits. Empty when nothing is left to read.
 */
export function nameFromEmail(email: string): string {
  const local = (email.trim().split("@")[0] ?? "").split("+")[0] ?? "";
  return local
    .split(/[._]+/)
    .filter((word) => word !== "" && !/^\d+$/.test(word))
    .map((word) =>
      word
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("-"),
    )
    .join(" ")
    // A list's one name is the name they go by, which the server keeps to DISPLAY_NAME_MAX.
    .slice(0, DISPLAY_NAME_MAX)
    .trim();
}

const address = z.string().email();

/**
 * Addresses as an admin pastes or types them: separated by commas, semicolons, spaces or new lines,
 * and also as a mail client copies them, `Jane Doe <jane@example.com>`, when the name is taken too.
 * Each address comes once, in lower case, which is how the server keeps it.
 */
export function parseAddresses(text: string): { people: BulkPerson[]; invalid: string[] } {
  const people: BulkPerson[] = [];
  const invalid: string[] = [];
  const add = (raw: string, name: string) => {
    const email = raw.trim().replace(/^["'(]+|["')]+$/g, "").toLowerCase();
    if (email === "") return;
    if (!address.safeParse(email).success) invalid.push(raw.trim());
    else if (!people.some((p) => p.email === email)) people.push({ email, display_name: name });
  };
  for (const segment of text.split(/[,;\n]+/)) {
    const named = /^(.*)<([^<>]+)>\s*$/.exec(segment.trim());
    if (named) add(named[2]!, named[1]!.trim().replace(/^["']+|["']+$/g, "").trim());
    else for (const token of segment.split(/\s+/)) add(token, "");
  }
  return { people, invalid };
}

/**
 * The list once `text` is added to it: each new address with the name it came with, or one read
 * from the address. What could not be added stays in `left`, for the box it came from, and
 * `problem` says why: not an address, or past the most one batch invites.
 */
export function addAddresses(
  current: readonly BulkPerson[],
  text: string,
  limit = MAX_BULK_INVITATIONS,
): { people: BulkPerson[]; left: string; problem: string | null } {
  const { people: parsed, invalid } = parseAddresses(text);
  const people = [...current];
  const overflow: string[] = [];
  for (const person of parsed) {
    if (people.some((p) => p.email === person.email)) continue;
    if (people.length >= limit) overflow.push(person.email);
    else people.push({ ...person, display_name: person.display_name || nameFromEmail(person.email) });
  }
  const problems = [
    invalid.length === 1 ? `"${invalid[0]}" is not an email address.` : "",
    invalid.length > 1 ? `These are not email addresses: ${invalid.join(", ")}.` : "",
    overflow.length > 0
      ? `You can invite up to ${limit} people at once. Send these, then add the rest.`
      : "",
  ].filter(Boolean);
  return {
    people,
    left: [...invalid, ...overflow].join("\n"),
    problem: problems.length > 0 ? problems.join(" ") : null,
  };
}

/**
 * The body of `POST /invitations/bulk`: the list's terms, and for anybody given their own, theirs
 * beside their address. A name left blank is sent as none. A row's role brings the scope that role
 * needs with it, since the list's belongs to the list's role.
 */
export function bulkInvitationPayload(
  people: readonly BulkPerson[],
  values: PersonTerms,
  assignable: readonly Pick<RoutingSkill, "slug">[],
): InvitationBulkInput {
  return {
    people: people.map((p) => ({
      email: p.email,
      display_name: p.display_name.trim() || null,
      ...(p.terms ? invitationTerms(p.terms, assignable) : {}),
    })),
    ...invitationTerms(values, assignable),
  };
}

/** How a batch went, person by person: emailed, the email failed, link only, or not invited. */
export function bulkOutcomes(results: readonly InvitationBulkResult[]) {
  const counts = { emailed: 0, failed: 0, link: 0, skipped: 0 };
  for (const result of results) {
    if (result.invitation) counts[emailOutcome(result.invitation)] += 1;
    else counts.skipped += 1;
  }
  return { ...counts, invited: results.length - counts.skipped };
}

/** The role everybody in a batch joins on, or null when they do not all join on one. */
export function batchRole(results: readonly InvitationBulkResult[]): Role | null {
  const roles = new Set(results.flatMap((r) => (r.invitation ? [r.invitation.role] : [])));
  return roles.size === 1 ? ([...roles][0] as Role) : null;
}

/** "3 emailed, 1 email failed, 2 not invited": only the outcomes that happened. */
export function bulkSummary(results: readonly InvitationBulkResult[]): string {
  const c = bulkOutcomes(results);
  return [
    c.emailed ? `${c.emailed} emailed` : "",
    c.failed ? `${c.failed} email${c.failed === 1 ? "" : "s"} failed` : "",
    c.link ? `${c.link} to send yourself` : "",
    c.skipped ? `${c.skipped} not invited` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "5 minutes", "47 hours", "3 days". Under two days it stays in hours, so the last two days of a
 *  72-hour link are read against the clock. */
export function spanWords(ms: number): string {
  const abs = Math.abs(ms);
  const [count, unit] =
    abs < HOUR
      ? [Math.max(1, Math.round(abs / MINUTE)), "minute"]
      : abs < 2 * DAY
        ? [Math.round(abs / HOUR), "hour"]
        : [Math.round(abs / DAY), "day"];
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

export interface StatusView {
  label: string;
  tone: "accent" | "warning" | "success";
  detail: string;
}

/** An invitation's status as its row shows it, with how long it has left or since when. */
export function invitationStatus(
  invitation: Pick<Invitation, "status" | "expires_at" | "accepted_at">,
  now = Date.now(),
): StatusView {
  const expires = new Date(invitation.expires_at).getTime();
  if (invitation.status === "accepted") {
    const at = invitation.accepted_at ? new Date(invitation.accepted_at).getTime() : now;
    return { label: "Accepted", tone: "success", detail: `${spanWords(now - at)} ago` };
  }
  if (invitation.status === "expired") {
    return { label: "Expired", tone: "warning", detail: `${spanWords(now - expires)} ago` };
  }
  return { label: "Pending", tone: "accent", detail: `Expires in ${spanWords(expires - now)}` };
}

/** How the last send's email went: taken by SendGrid or the SMTP server, refused, or never tried
 *  because the server sends no email and the admin shared the link. */
export type EmailOutcome = "emailed" | "failed" | "link";

export function emailOutcome(invitation: Pick<Invitation, "emailed_at" | "email_error">): EmailOutcome {
  if (invitation.emailed_at) return "emailed";
  if (invitation.email_error) return "failed";
  return "link";
}

export const EMAIL_LABELS: Record<EmailOutcome, string> = {
  emailed: "Emailed",
  failed: "Email failed",
  link: "Link only",
};

/** The action that sends an invitation again: "Re-invite" once it has expired. An accepted one has
 *  nothing left to send. */
export function resendLabel(invitation: Pick<Invitation, "status">): string | null {
  if (invitation.status === "accepted") return null;
  return invitation.status === "expired" ? "Re-invite" : "Send again";
}


export const INVITATIONS_PAGE_SIZE = 25;

/** Search the whole visible list before paging, preserving the API's newest-first order. */
export function invitationPage<T extends Pick<Invitation, "email" | "display_name" | "status">>(
  invitations: readonly T[],
  search: string,
  status: "all" | Invitation["status"],
  requestedPage: number,
  pageSize = INVITATIONS_PAGE_SIZE,
) {
  const query = search.trim().toLowerCase();
  const matching = invitations.filter((invitation) =>
    (status === "all" || invitation.status === status) &&
    (!query || invitation.email.toLowerCase().includes(query) ||
      (invitation.display_name ?? "").toLowerCase().includes(query)),
  );
  // Deleting the last row of a page or a refresh can reduce the number of pages.
  const page = Math.min(Math.max(0, requestedPage), Math.max(0, Math.ceil(matching.length / pageSize) - 1));
  return { rows: matching.slice(page * pageSize, (page + 1) * pageSize), total: matching.length, page };
}
